import { DatePipe, DecimalPipe } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';

import { TranslatePipe } from '@ngx-translate/core';

import { VOneButtonComponent } from '@shared-components';
import { STRING_CONSTANTS } from '@shared-constants';
import { Note } from '@shared-models';
import { sumAppliedPayments } from '@shared-utils';

import { IInvoicePayments } from 'src/app/@interfaces/payments.interface';
import { IQuota } from 'src/app/@interfaces/quotation.interface';

interface NoteRow {
  note: Note;
  isCredit: boolean;
  number: string;
  signedTotal: number;
}

@Component({
  selector: 'v-one-notes-detail-modal',
  imports: [DatePipe, DecimalPipe, TranslatePipe, VOneButtonComponent],
  templateUrl: './notes-detail-modal.component.html',
  styleUrls: ['./notes-detail-modal.component.scss'],
})
export class NotesDetailModalComponent implements OnInit {
  @Input() idFactura = 0;
  @Input() notes: Note[] = [];
  // All four of these are already resolved by the time the "Notas" button on
  // the card is clickable (see card-body.component.ts's relatedNotes signal
  // and portfolio.component.ts's loadNotesSummary/loadPaymentStatuses bulk
  // loads), so the modal only renders already-loaded data instead of
  // re-fetching it — no invoice/notes/payments request fires on open.
  @Input() quotation = {} as IQuota;
  @Input() creditTotal = 0;
  @Input() debitTotal = 0;
  @Input() invoicePayments: IInvoicePayments[] = [];
  @Input() closeModal?: () => void;

  rows: NoteRow[] = [];

  ngOnInit(): void {
    this.rows = [...this.notes]
      .sort(
        (a, b) =>
          new Date(a.createAt || a.dueDate).getTime() -
          new Date(b.createAt || b.dueDate).getTime(),
      )
      .map((note) => this.toRow(note));
  }

  private toRow(note: Note): NoteRow {
    const isCredit = note.tipo_nota === 'Credito';
    // note.total isn't reliably stored as a positive magnitude — credit
    // notes are negated up front (see note-builder.service.ts) — so abs()
    // before reapplying the sign that's actually correct for display: a
    // credit note reduces the invoice, a debit note increases it.
    const magnitude = Math.abs(note.total ?? 0);

    return {
      note,
      isCredit,
      number: this.noteNumber(note, isCredit),
      signedTotal: isCredit ? -magnitude : magnitude,
    };
  }

  private noteNumber(note: Note, isCredit: boolean): string {
    const billyNumber = note.billyNotes?.[0]?.note_number;
    if (billyNumber && billyNumber !== STRING_CONSTANTS.PENDING) {
      return billyNumber;
    }

    const prefix = isCredit ? 'NC' : 'ND';
    return `${prefix}-${String(note.id_nota).padStart(3, '0')}`;
  }

  get invoiceConsecutiveNumber(): string {
    return (
      this.quotation.contfac?.invoice || this.quotation.contfac?.quotation || ''
    );
  }

  get dianInvoiceNumber(): string {
    const electronicNumber = this.quotation.billyInvoice?.invoice_number;

    if (
      !electronicNumber ||
      electronicNumber.trim() === STRING_CONSTANTS.PENDING
    ) {
      return '';
    }

    return electronicNumber;
  }

  get customerDisplayName(): string {
    const customer = this.quotation.cliente;
    if (!customer?.nombre) {
      return '';
    }

    return customer.doc_nit
      ? `${customer.nombre} · NIT ${customer.doc_nit}`
      : customer.nombre;
  }

  get originalTotal(): number {
    return this.quotation.venta?.[0]?.total || 0;
  }

  // Debit notes increase the balance and credit notes decrease it, so this
  // nets them by subtraction. Keep this sign convention in sync with
  // PaymentModalComponent.ncNdAdjustment and CardBodyComponent.notesSummary.
  get netAdjustment(): number {
    return this.debitTotal - this.creditTotal;
  }

  get adjustedTotal(): number {
    return this.originalTotal + this.netAdjustment;
  }

  get paidTotal(): number {
    return sumAppliedPayments(this.invoicePayments);
  }

  get outstandingBalance(): number {
    return Math.max(0, this.adjustedTotal - this.paidTotal);
  }

  get hasOutstandingBalance(): boolean {
    return this.outstandingBalance > 0.01;
  }

  protected handleCloseButtonClick(): void {
    this.closeModal?.();
  }
}
